/**
 * app-recovery-bucket.js — the decryption recovery bucket (App slice).
 *
 * Extracted from js/app-core.js (September 2026) to bring that file back under
 * its audit-docs ceiling after the client-switch flush grew. It began as a pure
 * move, and the call sites it left behind gained a `typeof` guard, so a slice
 * that fails to load degrades to "no parking" rather than turning a failed
 * decrypt into a thrown load. Since then (intakecore/B1) the park reports
 * whether it landed, and _parkFormReplacedWhileLocked joined it.
 *
 * NOT a plugin IIFE — this is an App slice (Object.assign; see the "App
 * Assembly" table in CLAUDE.md). It must load EAGERLY, after app-core.js:
 * App.load() parks ciphertext at boot.
 */
Object.assign(App, {
    // ═══════════════════════════════════════════════════════════════════════
    // Decryption recovery bucket — when CryptoHelper.decrypt returns null, we
    // park the original ciphertext here rather than letting the next save
    // silently overwrite it. If the user recovers a key/passphrase later,
    // these blobs are their path back.
    //
    // Capped at 20 entries (FIFO eviction) to prevent quota bloat. Local-only;
    // never synced to cloud.
    // ═══════════════════════════════════════════════════════════════════════
    RECOVERY_CAP: 20,

    // Returns true when the blob is in the bucket (parked now or already), so a
    // caller that promises the agent their data was set aside can check first.
    // `message` replaces the log line for a blob that is parked but not corrupt,
    // and `opts.deliberate` marks a park the agent asked for (a confirmed
    // reset): it logs as a success, because an `error|crypto` entry is one no
    // later success clears, and it would hold the header pill red for 12 hours
    // over something that did not go wrong.
    _parkCiphertextForRecovery(originalKey, ciphertext, reason, message, opts) {
        if (!ciphertext) return false;
        let parked = false;
        try {
            const raw = localStorage.getItem(STORAGE_KEYS.DECRYPTION_RECOVERY);
            const bucket = raw ? JSON.parse(raw) : [];
            // Don't park an identical ciphertext twice — same blob failing on
            // every reload would otherwise fill the bucket with duplicates.
            if (bucket.some(e => e && e.ciphertext === ciphertext && e.originalKey === originalKey)) {
                return true;
            }
            bucket.unshift({
                originalKey,
                ciphertext,
                failedAt: new Date().toISOString(),
                reason: reason || 'unspecified',
            });
            if (bucket.length > this.RECOVERY_CAP) bucket.length = this.RECOVERY_CAP;
            localStorage.setItem(STORAGE_KEYS.DECRYPTION_RECOVERY, JSON.stringify(bucket));
            parked = true;
            console.warn(`[Recovery] Parked ciphertext from ${originalKey} (reason: ${reason}). Bucket now has ${bucket.length} entr${bucket.length === 1 ? 'y' : 'ies'}.`);
            // Surface to the new ActivityLog so the header status pill turns
            // red and the user has a single place to find out their key/
            // fingerprint changed silently. Without this, decryption failures
            // were invisible outside of DevTools and a tiny inline toast.
            if (window.ActivityLog && typeof window.ActivityLog.add === 'function') {
                const entry = (opts && opts.deliberate)
                    ? { type: 'save', area: 'crypto', ok: true, message: message || 'Data set aside for recovery' }
                    : { type: 'error', area: 'crypto', ok: false, message: message || 'Decryption failed, ciphertext parked for recovery' };
                window.ActivityLog.add(Object.assign(entry, {
                    detail: `${originalKey} · ${reason} · ${bucket.length} blob${bucket.length === 1 ? '' : 's'} in recovery bucket. Inspect via App.getRecoveryBlobs().`,
                }));
            }
        } catch (e) {
            console.error('[Recovery] Failed to park ciphertext:', e);
        }
        return parked;
    },

    // The agent typed into the intake while a locked boot had deferred loading
    // the saved one (_deferLockedLoad, js/app-core.js). On unlock their edits
    // win and js/vault-ui.js replays them over FORM, so the stored copy goes to
    // the bucket first. It decrypts fine, hence its own reason and log line,
    // and the toast only says it was set aside once the bucket holds it.
    _parkFormReplacedWhileLocked() {
        const blob = localStorage.getItem(this.storageKey);
        if (!blob) return false;
        const parked = this._parkCiphertextForRecovery(this.storageKey, blob, 'replaced-by-edits-made-while-locked',
            'Saved intake set aside, edits made while the vault was locked replaced it');
        const msg = parked
            ? 'Your edits were kept. The intake saved earlier was set aside in the recovery bucket.'
            : 'Your edits were kept, but the intake saved earlier could not be set aside.';
        this.toast(msg, { type: parked ? 'warning' : 'error', duration: 6000 });
        return parked;
    },

    // Returns the recovery bucket contents. Primarily for debug inspection
    // and future recovery UI — call from console: App.getRecoveryBlobs().
    getRecoveryBlobs() {
        try {
            const raw = localStorage.getItem(STORAGE_KEYS.DECRYPTION_RECOVERY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            console.error('[Recovery] Failed to read bucket:', e);
            return [];
        }
    },

    // Clear the entire bucket. For use when the user has confirmed all parked
    // blobs are no longer needed (e.g., they've recovered what they wanted or
    // accepted the loss). Defensive — requires explicit confirm.
    clearRecoveryBlobs() {
        if (!confirm('Permanently delete all parked recovery blobs? This cannot be undone.')) return 0;
        const count = this.getRecoveryBlobs().length;
        localStorage.removeItem(STORAGE_KEYS.DECRYPTION_RECOVERY);
        this.toast(`🗑 Cleared ${count} recovery blob${count === 1 ? '' : 's'}`);
        return count;
    },
});
